import type { PhaseProgressionPreferences } from '@smartphone-tycoon/shared-core';

/**
 * Simple Preference Service
 * 
 * Replaces the complex waterfall preference system with a straightforward approach:
 * - If global preference is "ask", show prompt
 * - If global preference is anything else, use it directly
 * - No complex state tracking or investment strike counts
 */

export type PhaseChoice = 'proceed' | 'minor' | 'major';

/**
 * Determines if a phase completion prompt should be shown to the user
 */
export function shouldShowPhasePrompt(
  phase: keyof PhaseProgressionPreferences,
  globalPreferences: PhaseProgressionPreferences
): boolean {
  return globalPreferences[phase] === 'ask';
}

/**
 * Gets the automatic choice for a phase (when user has set a preference)
 * Returns null if user should be prompted (preference is "ask")
 */
export function getPhaseChoice(
  phase: keyof PhaseProgressionPreferences,
  globalPreferences: PhaseProgressionPreferences
): PhaseChoice | null {
  const preference = globalPreferences[phase];
  
  if (preference === 'ask') {
    return null; // User should be prompted
  }
  
  // Return the preference directly
  return preference as PhaseChoice;
}

/**
 * Gets display-friendly label for a preference choice
 */
export function getPreferenceLabel(choice: string): string {
  switch (choice) {
    case 'proceed': return 'Auto-Proceed';
    case 'minor': return 'Minor Investment';
    case 'major': return 'Major Investment';
    case 'ask': return 'Ask Each Time';
    default: return choice;
  }
}

/**
 * Gets CSS color class for preference display
 */
export function getPreferenceColor(choice: string): string {
  switch (choice) {
    case 'proceed': return 'bg-theme-success/10 text-theme-success';
    case 'minor': return 'bg-theme-info/10 text-theme-info';
    case 'major': return 'bg-theme-accent/10 text-theme-accent';
    case 'ask': return 'bg-theme-bg-tertiary text-theme-text-secondary';
    default: return 'bg-theme-bg-tertiary text-theme-text-secondary';
  }
}

/**
 * Simplified preference information for UI display
 */
export interface SimplePreferenceInfo {
  preference: string;
  label: string;
  color: string;
}

/**
 * Gets preference information for a specific phase
 */
export function getPreferenceInfo(
  phase: keyof PhaseProgressionPreferences,
  globalPreferences: PhaseProgressionPreferences
): SimplePreferenceInfo {
  const preference = globalPreferences[phase];
  
  return {
    preference,
    label: getPreferenceLabel(preference),
    color: getPreferenceColor(preference)
  };
}